import {ToggleGroup, ToggleGroupItem} from '@/base/ui/toggleGroup'
import {cn} from '@/lib/utils'
import React from 'react'
import {IWuSwitcherOptions} from '../types/IWuSwitcherOptions'

interface IToggleProps<T> {
  value: string
  options: IWuSwitcherOptions<T>
  onChange: (e: string) => void
  disabled?: boolean
  dir?: 'ltr' | 'rtl'
}
export const _Toggle = React.forwardRef(
  <T,>(
    props: IToggleProps<T>,
    ref: React.Ref<HTMLDivElement>,
  ): React.JSX.Element => {
    const {options, value, disabled, dir, onChange} = props

    const itemClass = (itemValue: string): string =>
      cn(
        'p-0 w-fit text-xs text-gray-lead h-fit font-normal',
        disabled &&
          'cursor-not-allowed data-[state=on]:text-gray-subtle text-gray-subtle',
        itemValue === value && 'text-primary font-medium',
        disabled && value === itemValue && 'text-gray-lead',
        'wu-switcher-toggle-item',
      )
    const groupClass = cn(
      'w-fit gap-2',
      disabled && 'cursor-not-allowed',
      'wu-switcher-toggle-group',
    )
    const buttonClass = cn(
      'flex items-center justify-center w-8 h-3 p-0 border rounded-full cursor-pointer border-blue-p focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-soft focus-visible:ring-offset-2',
      disabled && 'border-gray-subtle cursor-not-allowed',
      'wu-switcher-toggle-button',
    )
    const knobClass = cn(
      'w-2 h-2 bg-blue-p rounded-full transition-transform duration-300 ease-in-out',
      value === 'opt1' && dir !== 'rtl' && '-translate-x-[10px]',
      value === 'opt1' && dir === 'rtl' && 'translate-x-[10px]',
      value === 'opt2' && dir !== 'rtl' && 'translate-x-[10px]',
      value === 'opt2' && dir === 'rtl' && '-translate-x-[10px]',
      disabled && 'hidden',
      'wu-switcher-toggle-knob',
    )

    return (
      <>
        <ToggleGroup
          ref={ref}
          type="single"
          value={value}
          disabled={disabled}
          dir={dir}
          className={groupClass}
          onValueChange={onChange}
        >
          <ToggleGroupItem value="opt1" className={itemClass('opt1')}>
            <div className="max-w-[136px] overflow-hidden text-ellipsis text-nowrap text-right wu-switcher-toggle-item-label">
              {options[0].label}
            </div>
          </ToggleGroupItem>
          <button
            onClick={() => onChange(value === 'opt1' ? 'opt2' : 'opt1')}
            className={buttonClass}
            disabled={disabled}
            aria-label="knob"
            dir={dir}
          >
            <div className={knobClass} role="knob" dir={dir}></div>
          </button>
          <ToggleGroupItem value="opt2" className={itemClass('opt2')}>
            <div className="max-w-[136px] overflow-hidden text-ellipsis text-nowrap wu-switcher-toggle-item-label">
              {options[1].label}
            </div>
          </ToggleGroupItem>
        </ToggleGroup>
      </>
    )
  },
)

_Toggle.displayName = '_Toggle'
